import { useState } from "react";
import { AddressPanel } from "./address-panel";
import type { AttestationTrustRoot, SignatureVerifier } from "./attestation";
import { CheckoutFrame } from "./checkout-frame";
import { CheckoutClosed, CheckoutDenied, CheckoutError, CheckoutLoading, CheckoutNotActive, CheckoutRateLimited } from "./checkout-states";
import { DeclarationForm } from "./declaration-form";
import { AntiPhishingNotice, PayeeAndInvoice } from "./payee-invoice";
import { ReceiptPanel } from "./receipt-panel";
import { RefundPanel } from "./refund-panel";
import { StatusPanel } from "./status-panel";
import type { PayCheckoutView, PayDeclarationView } from "./pay-types";
import { usePayCheckout, usePayStatus } from "./use-pay";

/**
 * The public checkout, at `/pay`.
 *
 * One component per state of the link, so there is exactly one place that
 * decides what a given refusal looks like and no state can leak a value the
 * refusal did not include. The four states a payer can land on without a usable
 * link — not-active, closed, rate-limited, error — are all reachable from the
 * same entry point and none of them says whether the token exists.
 *
 * `trustRoot` and `verifier` are injectable for the unit suite only; production
 * passes neither, and the attestation gate then uses the key pinned in
 * `attestation.ts` (which is empty in this build — see that file).
 */
export function PayCheckout({
  token,
  trustRoot,
  verifier,
}: {
  token: string | null;
  trustRoot?: AttestationTrustRoot;
  verifier?: SignatureVerifier;
}) {
  const { state, refresh } = usePayCheckout(token);

  if (state.kind === "loading") {
    return (
      <CheckoutFrame centered>
        <CheckoutLoading />
      </CheckoutFrame>
    );
  }

  if (state.kind === "not-active") {
    return (
      <CheckoutFrame centered>
        <CheckoutNotActive />
      </CheckoutFrame>
    );
  }

  if (state.kind === "closed") {
    return (
      <CheckoutFrame centered>
        <CheckoutClosed />
      </CheckoutFrame>
    );
  }

  if (state.kind === "rate-limited") {
    return (
      <CheckoutFrame centered>
        <CheckoutRateLimited onRetry={refresh} />
      </CheckoutFrame>
    );
  }

  if (state.kind === "denied") {
    return (
      <CheckoutFrame centered>
        <CheckoutDenied error={state.error} />
      </CheckoutFrame>
    );
  }

  if (state.kind === "error") {
    return (
      <CheckoutFrame centered>
        <CheckoutError error={state.error} status={state.status} onRetry={refresh} />
      </CheckoutFrame>
    );
  }

  // A ready checkout exists only behind a token, but narrowing `token` needs
  // the check rather than an assertion.
  if (token === null) {
    return (
      <CheckoutFrame centered>
        <CheckoutNotActive />
      </CheckoutFrame>
    );
  }

  return <ReadyCheckout token={token} checkout={state.checkout} trustRoot={trustRoot} verifier={verifier} />;
}

/**
 * The checkout for a link the service answered for.
 *
 * The declaration is held here rather than re-read: it is the only piece of
 * this page the payer themselves changes, and re-fetching the whole checkout
 * after saving one form would replace the payment status under a poll that is
 * already running.
 */
function ReadyCheckout({
  token,
  checkout,
  trustRoot,
  verifier,
}: {
  token: string;
  checkout: PayCheckoutView;
  trustRoot?: AttestationTrustRoot;
  verifier?: SignatureVerifier;
}) {
  const [declaration, setDeclaration] = useState<PayDeclarationView | null>(checkout.declaration);
  const { payment, polling, stale, refresh } = usePayStatus(token, checkout.payment);

  const final = payment.status === "paid" || payment.status === "overpaid" || payment.status === "paid_late";

  return (
    <CheckoutFrame>
      <PayeeAndInvoice checkout={checkout} />
      <AntiPhishingNotice />

      <DeclarationForm
        token={token}
        declaration={declaration}
        onSaved={setDeclaration}
      />

      <AddressPanel
        token={token}
        networks={checkout.networks}
        declarationReady={declaration !== null}
        trustRoot={trustRoot}
        verifier={verifier}
      />

      <StatusPanel payment={payment} polling={polling} stale={stale} onRefresh={refresh} />

      <ReceiptPanel token={token} final={final} />

      {checkout.refund && <RefundPanel token={token} refund={checkout.refund} />}
    </CheckoutFrame>
  );
}
